/**
 * MySQL string-literal quoting and its exact inverse, for renderer code.
 *
 * MySQL — unlike the engines whose providers only double the quote — treats a
 * backslash as an ESCAPE character inside a string literal unless
 * `NO_BACKSLASH_ESCAPES` is set, which it is not by default. Whatever writes a
 * literal has to encode it, and whatever reads one back (the Table Designer's
 * legacy drafts store raw `'…'` SQL text) has to decode it, or the value
 * round-trips through the pair with its escapes doubled.
 *
 * The Electron side has the same pair (`quoteMysqlStringLiteral` +
 * `readMysqlStringLiteral`, both in
 * `electron/database-blueprint/ddl-parse/mysqlStringLiteral.ts`); renderer
 * code must not import from `electron/`, hence this module.
 */

/** Control characters MySQL's default sql_mode reads back through its escape sequences. */
const MYSQL_CONTROL_ESCAPES: Record<string, string> = {
  '\n': '\\n', '\r': '\\r', '\t': '\\t', '\u0000': '\\0', '\u0008': '\\b', '\u001a': '\\Z',
};

/**
 * Quote a string as a MySQL string literal. Doubling only the quote would let
 * `C:\path` reach the server as `C:path`, and a trailing backslash would
 * swallow the closing quote. Control characters are encoded with their escape
 * sequences so an emitted CREATE stays one line per column.
 */
export function quoteMysqlLiteral(value: string): string {
  const escaped = value
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "''")
    // eslint-disable-next-line no-control-regex
    .replace(/[\n\r\t\u0000\u0008\u001a]/g, (ch) => MYSQL_CONTROL_ESCAPES[ch]);
  return `'${escaped}'`;
}

/** MySQL's documented backslash escapes
 *  (https://dev.mysql.com/doc/refman/8.0/en/string-literals.html). `\%` / `\_`
 *  deliberately keep BOTH characters — MySQL leaves them intact so the pattern
 *  metacharacter survives into LIKE. Anything not listed decodes to the escaped
 *  character itself. */
const MYSQL_ESCAPES: Record<string, string> = {
  '0': '\0', "'": "'", '"': '"', b: '\b', n: '\n', r: '\r', t: '\t', Z: '\u001a',
  '\\': '\\', '%': '\\%', _: '\\_',
};

/**
 * Decoded content of `text` when it is exactly ONE complete single-quoted MySQL
 * string literal, `undefined` otherwise (unterminated, not a literal, or with
 * anything after the closing quote — `'abc' || 'def'` is an expression, not a
 * literal whose content spans the gap). Double-quoted text is deliberately not
 * accepted: under `ANSI_QUOTES` it is an identifier, not a literal.
 */
export function decodeMysqlLiteral(text: string): string | undefined {
  if (text.length < 2 || !text.startsWith("'")) return undefined;
  let out = '';
  for (let i = 1; i < text.length; i++) {
    const char = text[i];
    if (char === '\\') {
      const next = text[i + 1];
      if (next === undefined) return undefined;
      out += MYSQL_ESCAPES[next] ?? next;
      i++;
      continue;
    }
    if (char === "'") {
      if (text[i + 1] === "'") {
        out += "'";
        i++;
        continue;
      }
      return i === text.length - 1 ? out : undefined;
    }
    out += char;
  }
  return undefined;
}
